package com.dignicate.web.style

import com.dignicate.web.css.BorderStyle
import com.dignicate.web.css.ColorValue
import com.dignicate.web.css.FontWeightValue
import com.dignicate.web.css.Isolation
import com.dignicate.web.css.OutlineStyle
import com.dignicate.web.css.PointerEvents
import com.dignicate.web.css.ThemeVar
import com.dignicate.web.css.TimingFunction
import com.dignicate.web.css.TransitionProperty
import com.dignicate.web.css.WillChange
import com.dignicate.web.css.Contain
import com.dignicate.web.css.Cursor
import com.dignicate.web.css.FlexBasis
import com.dignicate.web.css.FlexDirection
import com.dignicate.web.css.FlexGrow
import com.dignicate.web.css.FlexShrink
import com.dignicate.web.css.JustifyContent
import com.dignicate.web.css.TextAlign
import com.dignicate.web.css.WidthValue
import com.dignicate.web.css.backdropFilter
import com.dignicate.web.css.background
import com.dignicate.web.css.backgroundColor
import com.dignicate.web.css.borderBottom
import com.dignicate.web.css.borderBox
import com.dignicate.web.css.borderColorVar
import com.dignicate.web.css.borderTop
import com.dignicate.web.css.borderVar
import com.dignicate.web.css.boxShadow
import com.dignicate.web.css.boxShadowVar
import com.dignicate.web.css.color
import com.dignicate.web.css.contain
import com.dignicate.web.css.cursor
import com.dignicate.web.css.define
import com.dignicate.web.css.defineColor
import com.dignicate.web.css.defineRgba
import com.dignicate.web.css.flex
import com.dignicate.web.css.flexDirection
import com.dignicate.web.css.fontWeight
import com.dignicate.web.css.gridTemplateAutoFitMinmax
import com.dignicate.web.css.inset
import com.dignicate.web.css.isolation
import com.dignicate.web.css.justifyContent
import com.dignicate.web.css.lineHeightUnitless
import com.dignicate.web.css.marginXAuto
import com.dignicate.web.css.ms
import com.dignicate.web.css.opacity
import com.dignicate.web.css.opaque
import com.dignicate.web.css.outline
import com.dignicate.web.css.pointerEvents
import com.dignicate.web.css.rgbaColor
import com.dignicate.web.css.rightSafeAreaMax
import com.dignicate.web.css.textAlign
import com.dignicate.web.css.textDecorationNone
import com.dignicate.web.css.topCalcVarDivide
import com.dignicate.web.css.transform
import com.dignicate.web.css.transformOrigin
import com.dignicate.web.css.transition
import com.dignicate.web.css.translateY
import com.dignicate.web.css.transparent
import com.dignicate.web.css.width
import com.dignicate.web.css.willChange
import com.dignicate.web.css.zIndex
import com.dignicate.web.css.zIndexMax
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.StyleSheet
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.paddingLeft
import org.jetbrains.compose.web.css.paddingRight
import org.jetbrains.compose.web.css.paddingTop
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.right
import org.jetbrains.compose.web.css.textDecoration
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.css.color as colorProp

object AppStyleSheet : StyleSheet() {

    init {
        ":root" style {
            defineColor(ThemeVar.Bg, "#ffffff")
            defineColor(ThemeVar.Fg, "#111111")
            defineRgba(ThemeVar.Muted, 0,0,0,0.64)
            defineColor(ThemeVar.CardBg, "#f2f3f5")
            defineRgba(ThemeVar.CardBorder, 0,0,0,0.08)
            define(ThemeVar.Shadow1, "0 6px 18px rgba(0,0,0,.06)")
            define(ThemeVar.Shadow1Hover, "0 10px 28px rgba(0,0,0,.10)")
            define(ThemeVar.HeaderHeight, "56px")
        }
        "[data-theme='dark']" style {
            defineColor(ThemeVar.Bg, "#0b0c0d")
            defineColor(ThemeVar.Fg, "#e8e8e8")
            defineRgba(ThemeVar.Muted, 255,255,255,0.72)
            defineColor(ThemeVar.CardBg, "#16181a")
            defineRgba(ThemeVar.CardBorder, 255,255,255,0.12)
            define(ThemeVar.Shadow1, "0 6px 18px rgba(0,0,0,.50)")
            define(ThemeVar.Shadow1Hover, "0 10px 28px rgba(0,0,0,.65)")
            define(ThemeVar.HeaderHeight, "56px")
        }

        // 画面外（ラバー領域）も含めて背景をテーマ色に合わせる
        "html, body" style {
            backgroundColor(ThemeVar.Bg)
            color(ColorValue.Var(ThemeVar.Fg))
            // ensure full-height so percent-based min-height on .site-root works
            height(100.percent)
            minHeight(100.percent)
            margin(0.px)
        }

        // prevent horizontal overflow caused by wide elements
        "body" style {
            property(
                "overflow-x",
                "hidden"
            )
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            flex(FlexGrow(1), FlexShrink(1), FlexBasis.Keyword.Auto)
        }

        // Global link styles
        "html a" style {
            transition {
                t(TransitionProperty.COLOR, duration = ms(150), timing = TimingFunction.EaseInOut)
                t(TransitionProperty.BOX_SHADOW, duration = ms(150), timing = TimingFunction.EaseInOut)
            }
        }

        "html[data-theme='light'] a, html[data-theme='light'] a:visited" style {
            colorProp(Color("#0073e6"))
            textDecorationNone()
        }

        "html[data-theme='light'] a:hover" style {
            colorProp(Color("#005bbd"))
            textDecoration("underline")
        }

        "html[data-theme='light'] a:focus-visible" style {
            outline(OutlineStyle.None)
            borderRadius(4.px)
            boxShadow(0.px, 0.px, 0.px, 3.px, rgbaColor(0, 115, 230, 0.4))
        }

        "html[data-theme='dark'] a, html[data-theme='dark'] a:visited" style {
            colorProp(Color("#3399ff"))
            textDecorationNone()
        }

        "html[data-theme='dark'] a:hover" style {
            colorProp(Color("#66bfff"))
            textDecoration("underline")
        }

        "html[data-theme='dark'] a:focus-visible" style {
            outline(OutlineStyle.None)
            borderRadius(4.px)
            boxShadow(0.px, 0.px, 0.px, 3.px, rgbaColor(51, 153, 255, 0.5))
        }

        // セクションの上下余白
        ".section" style { padding(72.px, 0.px) }

        // 中央寄せ・最大幅
        ".container" style {
            maxWidth(1200.px)
            marginXAuto(0.px)
            padding(0.px, 80.px)
            borderBox()
        }

        // グリッド（auto-fit でレスポンシブ）
        ".cards-grid" style {
            display(DisplayStyle.Grid)
            gridTemplateAutoFitMinmax(260.px)
            gap(20.px)
        }

        ".service-card" style {
            background(ThemeVar.CardBg)
            borderVar(1.px, BorderStyle.Solid, ThemeVar.CardBorder)
            borderRadius(16.px)
            padding(20.px)
            boxShadowVar(ThemeVar.Shadow1)
            transition {
                t(
                    TransitionProperty.TRANSFORM,
                    ms(180)
                )
                t(
                    TransitionProperty.BOX_SHADOW,
                    ms(180)
                )
                t(
                    TransitionProperty.BORDER_COLOR,
                    ms(180)
                )
            }

            // Make card a vertical flex container so footer can stick to bottom
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
        ".service-card:hover" style {
            transform { translateY((-3).px) }
            boxShadowVar(ThemeVar.Shadow1Hover)
            borderColorVar(ThemeVar.CardBorder)
        }

        // Card title unified style
        ".card-title" style {
            fontSize(20.px)
            fontWeight(700)
            marginBottom(8.px)
            lineHeightUnitless(1.4)
            property(
                "letter-spacing",
                "0"
            )
        }

        // Card body should grow to push footer to the bottom
        ".service-card .card-body" style {
            // allow body to grow/shrink so footer stays pinned to card bottom
            flex(FlexGrow(1), FlexShrink(1), FlexBasis.Keyword.Auto)
        }

        // Card footer: minimal spacing from body and top padding
        ".service-card .card-footer" style {
            marginTop(12.px)
            paddingTop(8.px)
            // align left/right with card body (ensure no extra inner horizontal padding)
            paddingLeft(0.px)
            paddingRight(0.px)
            // subtle divider to visually attach footer to the card; theme-aware
            borderTop(1.px, BorderStyle.Solid, "var(--${ThemeVar.CardBorder.id})")
            // footer content arranged horizontally by default
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.FlexStart)
        }

        // Store badges row inside footer
        ".store-badges" style {
            display(DisplayStyle.Flex)
            gap(12.px)
            alignItems(AlignItems.Center)
            property(
                "flex-wrap",
                "wrap"
            )
            // ensure badges align with body content; don't add extra horizontal offset
            margin(0.px)
        }

        // Individual badge (applied to img element)
        ".store-badge" style {
            // unified height so badges appear consistent
            height(48.px)
            width(WidthValue.Auto)
            display(DisplayStyle.Block)
            cursor(Cursor.Pointer)
        }

        // Anchor wrapper for badges: hover/focus effects applied here (do NOT modify the image colors)
        ".store-badge-link" style {
            // Inline-flex isn't available via DisplayStyle in this Kotlin/Compose version, use explicit property
            display(DisplayStyle.LegacyInlineFlex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(4.px)
            borderRadius(8.px)
            transition {
                t(TransitionProperty.TRANSFORM, duration = ms(120), timing = TimingFunction.Ease)
                t(TransitionProperty.BOX_SHADOW, duration = ms(120), timing = TimingFunction.Ease)
            }
        }
        ".store-badge-link:hover" style {
            // light lift and soft shadow on hover (image colors untouched)
            property(
                "transform",
                "translateY(-3px)"
            )
            boxShadow(0.px, 6.px, 18.px, color = rgbaColor(0, 0, 0, 0.08))
        }
        ".store-badge-link:focus-visible" style {
            outline(OutlineStyle.None)
            boxShadow(0.px, 0.px, 0.px, 4.px, rgbaColor(0, 120, 215, 0.14))
        }

        // カード内リンク
        ".card-link" style {
            fontWeight(FontWeightValue.SemiBold)
            textDecorationNone()
            color(ColorValue.Inherit)
            borderBottom(1.px, BorderStyle.Solid, "currentColor")
        }
        ".card-link:hover" style { opacity(0.85) }

        ".theme-btn" style {
            background(ThemeVar.FabBg)
            color(ColorValue.Var(ThemeVar.FabFg))
            borderVar(1.px, BorderStyle.Solid, ThemeVar.FabBorder)
            borderRadius(12.px)
            padding(6.px, 12.px)
            fontSize(14.px)
            lineHeightUnitless(1)
            boxShadowVar(ThemeVar.FabShadow)
            transition {
                t(
                    TransitionProperty.TRANSFORM,
                    ms(150)
                )
                t(
                    TransitionProperty.BOX_SHADOW,
                    ms(150)
                )
                t(
                    TransitionProperty.BACKGROUND_COLOR,
                    ms(150)
                )
                t(
                    TransitionProperty.OPACITY,
                    ms(120)
                )
            }
        }
        ".theme-fab" style {
            position(Position.Fixed)
            topCalcVarDivide(ThemeVar.HeaderHeight, 2)
            rightSafeAreaMax(16.px)
            zIndexMax()
            backdropFilter { saturate(120); blur(
                6.px
            )
            }
            cursor(Cursor.Pointer)
        }
        ".theme-fab:hover" style {
            translateY((-1).px)
            boxShadowVar(ThemeVar.Shadow1Hover)
            borderColorVar(ThemeVar.CardBorder)
        }
        ".theme-fab:active" style { translateY(0.px) }
        ".theme-fab.hidden" style {
            transparent()
            pointerEvents(PointerEvents.None)
            translateY((-6).px)
        }

        // Settings modal styles
        ".settings-overlay" style {
            position(Position.Fixed)
            inset(0.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            // semi-transparent overlay that works on both themes
            background("rgba(0,0,0,0.45)")
            zIndex(3000)
        }

        ".settings-dialog" style {
            // glassmorphic gradient (light theme base)
            background("linear-gradient(180deg, rgba(255,255,255,0.85), rgba(245,245,245,0.85))")
            // primary border (theme-aware) and a subtle outline to create a two-layer boundary
            borderVar(1.px, BorderStyle.Solid, ThemeVar.CardBorder)
            property(
                "outline",
                "1px solid rgba(0,0,0,0.06)"
            )
            borderRadius(16.px)
            // stronger ambient shadow: deep soft shadow + gentle outer rim
            boxShadow {
                shadow(0.px, 18.px, 40.px, color = rgbaColor(2, 8, 23, 0.18))
                shadow(0.px, 2.px, 6.px, color = rgbaColor(2, 8, 23, 0.06))
            }
            padding(20.px)
            // sizing: prefer 360..420 but not exceed viewport
            property(
                "min-width",
                "360px"
            )
            maxWidth(420.px)
            property(
                "max-width",
                "calc(100vw - 32px)"
            )
            // start hidden/offset by default; will be animated via state classes
            transparent()
            property(
                "transform",
                "translateY(4px) scale(0.98)"
            )
            pointerEvents(PointerEvents.None)
            transition {
                t(TransitionProperty.OPACITY, duration = ms(180), timing = TimingFunction.Ease)
                t(TransitionProperty.TRANSFORM, duration = ms(180), timing = TimingFunction.Ease)
            }
            property(
                "backdrop-filter",
                "blur(14px)"
            )
            property(
                "-webkit-backdrop-filter",
                "blur(14px)"
            )
        }

        // dark theme override for dialog base background and outline
        "[data-theme='dark'] .settings-dialog" style {
            background("linear-gradient(180deg, rgba(30,30,30,0.88), rgba(18,18,18,0.88))")
            property(
                "outline",
                "1px solid rgba(255,255,255,0.06)"
            )
            boxShadow {
                shadow(0.px, 20.px, 48.px, color = rgbaColor(0, 0, 0, 0.45))
                shadow(0.px, 2.px, 8.px, color = rgbaColor(255, 255, 255, 0.02))
            }
        }

        // Overlay/dialog state classes for open/closing animations
        ".settings-overlay" style {
            // ensure we can animate opacity; default hidden
            transparent()
            transition {
                t(TransitionProperty.OPACITY, duration = ms(180), timing = TimingFunction.Ease)
            }
            pointerEvents(PointerEvents.None)
        }
        ".settings-overlay.is-open" style {
            opaque()
            pointerEvents(PointerEvents.Auto)
        }
        ".settings-overlay.is-closing" style {
            transparent()
            pointerEvents(PointerEvents.None)
        }

        ".settings-dialog.is-open" style {
            opaque()
            property(
                "transform",
                "translateY(0) scale(1)"
            )
            pointerEvents(PointerEvents.Auto)
        }
        ".settings-dialog.is-closing" style {
            transparent()
            property(
                "transform",
                "translateY(4px) scale(0.98)"
            )
            pointerEvents(PointerEvents.None)
        }

        /* High-specificity close button styling inside settings dialog to ensure visibility */
        ".settings-dialog .dialog-close-button" style {
            width(40.px)
            height(40.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            borderRadius(12.px)
            // light theme base: subtle dark overlay
            background("rgba(0,0,0,0.06)")
            property(
                "border",
                "1px solid rgba(0,0,0,0.14)"
            )
            color(ColorValue.Var(ThemeVar.Fg))
            property(
                "font-size",
                "18px"
            )
            lineHeightUnitless(1)
            property(
                "cursor",
                "pointer"
            )
            transition {
                t(TransitionProperty.BACKGROUND_COLOR, duration = ms(160), timing = TimingFunction.Ease)
                t(TransitionProperty.TRANSFORM, duration = ms(160), timing = TimingFunction.Ease)
                t(TransitionProperty.OPACITY, duration = ms(160), timing = TimingFunction.Ease)
            }
        }
        ".settings-dialog .dialog-close-button:hover" style {
            background("rgba(0,0,0,0.10)")
            opacity(0.98)
            property(
                "transform",
                "translateY(-1px)"
            )
        }
        ".settings-dialog .dialog-close-button:active" style {
            property(
                "transform",
                "translateY(1px)"
            )
        }
        ".settings-dialog .dialog-close-button:focus-visible" style {
            outline(OutlineStyle.None)
            boxShadow(0.px, 0.px, 0.px, 4.px, rgbaColor(0, 120, 215, 0.18))
        }

        /* Dark theme overrides for the close button to ensure contrast on dark dialog */
        "[data-theme='dark'] .settings-dialog .dialog-close-button" style {
            background("rgba(255,255,255,0.08)")
            property(
                "border",
                "1px solid rgba(255,255,255,0.18)"
            )
            color(ColorValue.Var(ThemeVar.Fg))
        }
        "[data-theme='dark'] .settings-dialog .dialog-close-button:hover" style {
            background("rgba(255,255,255,0.14)")
        }

        ".settings-header" style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            gap(12.px)
            marginBottom(12.px)
        }

        ".settings-title" style {
            fontSize(18.px)
            fontWeight(700)
            color(ColorValue.Var(ThemeVar.Fg))
        }

        ".settings-close" style {
            backgroundColor(rgbaColor(0,0,0,0.0))
            property("border", "0.px")
            padding(6.px)
            borderRadius(8.px)
            cursor(Cursor.Pointer)
            color(ColorValue.Var(ThemeVar.Muted))
        }
        ".settings-close:hover" style { opacity(0.9) }

        ".settings-section" style {
            marginTop(12.px)
            display(DisplayStyle.Block)
        }

        ".settings-label" style {
            fontSize(13.px)
            color(ColorValue.Var(ThemeVar.Muted))
            margin(0.px)
            marginBottom(8.px)
        }

        ".settings-select" style {
            width(100.percent)
            height(40.px)
            padding(8.px, 12.px)
            borderVar(1.px, BorderStyle.Solid, ThemeVar.CardBorder)
            borderRadius(8.px)
            // inputs should look like floating cards inside the glass dialog
            backgroundColor(ThemeVar.CardBg)
            color(ColorValue.Var(ThemeVar.Fg))
            property(
                "appearance",
                "none"
            )
        }

        ".settings-actions" style {
            marginTop(16.px)
        }

        ".settings-button" style {
            width(100.percent)
            padding(10.px, 12.px)
            borderRadius(10.px)
            background(ThemeVar.Fg)
            color(ColorValue.Var(ThemeVar.Bg))
            borderVar(1.px, BorderStyle.Solid, ThemeVar.CardBorder)
            fontWeight(FontWeightValue.SemiBold)
            cursor(Cursor.Pointer)
        }
        ".settings-button:hover" style { opacity(0.95) }

        // Site footer styles (company/links) - desktop-first; responsive tweaks later
        ".site-footer" style {
            // Theme-adaptive background and muted text
            backgroundColor(ThemeVar.CardBg)
            color(ColorValue.Var(ThemeVar.Muted))
            borderTop(1.px, BorderStyle.Solid, "var(${ThemeVar.CardBorder.id})")
            padding(24.px, 0.px)
        }

        ".footer-inner" style {
            maxWidth(1200.px)
            marginXAuto(0.px)
            padding(0.px, 20.px)
            borderBox()
        }

        ".footer-top" style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            gap(12.px)
            padding(8.px, 0.px)
        }

        ".footer-links" style {
            display(DisplayStyle.Flex)
            gap(16.px)
            alignItems(AlignItems.Center)
        }

        ".footer-link" style {
            color(ColorValue.Var(ThemeVar.Muted))
            textDecorationNone()
            cursor(Cursor.Pointer)
            property(
                "font-size",
                "14px"
            )
        }
        ".footer-link:hover" style {
            color(ColorValue.Var(ThemeVar.Fg))
            textDecoration("underline")
            opacity(0.95)
        }

        ".footer-bottom" style {
            borderTop(1.px, BorderStyle.Solid, "var(${ThemeVar.CardBorder.id})")
            marginTop(12.px)
            paddingTop(12.px)
            fontSize(13.px)
            color(ColorValue.Var(ThemeVar.Muted))
            textAlign(TextAlign.Center)
        }

        // Layout helpers to push footer to bottom (Sticky Footer pattern)
        ".site-root" style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            // support both percent and viewport-based min-height for reliability
            minHeight(100.percent)
            property(
                "min-height",
                "100vh"
            )
        }

        // main content area that grows to fill available space and allows scrolling inside
        ".site-main" style {
            flex(FlexGrow(1), FlexShrink(0), FlexBasis.Keyword.Auto)
            property("min-height", "0")
        }

        // compatibility alias if any code still uses .site-content
        ".site-content" style {
            flex(FlexGrow(1), FlexShrink(0), FlexBasis.Keyword.Auto)
            property("min-height", "0")
        }

        // Gear (settings) button used in header
        ".gear-button" style {
            width(44.px)
            height(44.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            borderRadius(12.px)
            backgroundColor(ThemeVar.CardBg)
            borderVar(1.px, BorderStyle.Solid, ThemeVar.CardBorder)
            color(ColorValue.Var(ThemeVar.Fg))
            boxShadowVar(ThemeVar.Shadow1)
            transition {
                t(
                    TransitionProperty.BACKGROUND_COLOR,
                    ms(160)
                )
                t(
                    TransitionProperty.TRANSFORM,
                    ms(160)
                )
                t(
                    TransitionProperty.OPACITY,
                    ms(160)
                )
            }
        }
        ".gear-button:hover" style {
            // subtle hover: slight lift and small opacity change; theme-specific contrast is handled by background color
            property(
                "transform",
                "translateY(-1px)"
            )
            opacity(0.98)
            property(
                "filter",
                "brightness(1.03)"
            )
        }
        // Dark-theme specific tweak (slightly stronger brighten on hover)
        "[data-theme='dark'] .gear-button:hover" style {
            property(
                "filter",
                "brightness(1.06)"
            )
        }
        ".gear-button:active" style {
            property(
                "transform",
                "translateY(1px)"
            )
        }

        ".gear-icon" style {
            fontSize(18.px)
            lineHeight(1.px)
            color(ColorValue.Var(ThemeVar.Fg))
            pointerEvents(PointerEvents.None)
        }

        // Focus visible for accessibility: make it clear when focus returns to the gear button
        ".gear-button:focus-visible" style {
            outline(OutlineStyle.None)
            boxShadow(0.px, 0.px, 0.px, 4.px, rgbaColor(0, 120, 215, 0.16))
        }

        // Ensure gear button inside header shows pointer cursor (header background stays default)
        ".site-header .gear-button" style {
            cursor(Cursor.Pointer)
        }

        // Header layering improvements for consistent blur+transform across browsers

        // 1) WebKit-based browsers (Safari) - keep existing backdrop-filter based implementation (no change)
        "@supports (-webkit-backdrop-filter: blur(1px))" style {
            ".site-header" style {
                position(Position.Relative)
                isolation(Isolation.Isolate)
                contain(Contain.Paint)
                // Preserve the original glass effect on Safari (backdrop-filter)
                property(
                    "backdrop-filter",
                    "saturate(180%) blur(6px)"
                )
                property(
                    "-webkit-backdrop-filter",
                    "saturate(180%) blur(6px)"
                )
                // keep the original translucent tint (theme-aware override remains in component)
            }

            // Hide the JS/CSS fallback overlay in Safari so there's no visual change / regression
            ".header-blur-overlay" style {
                display(DisplayStyle.None)
            }

            // Transition classes still apply (transform + opacity only)
            ".site-header.site-header--init" style {
                transparent()
                property(
                    "transform",
                    "translateY(6px)"
                )
            }
            ".site-header.site-header--ready" style {
                opaque()
                property(
                    "transform",
                    "translateY(0)"
                )
                transition {
                    t(TransitionProperty.TRANSFORM, duration = ms(220), customTiming = "cubic-bezier(0.2, 0.0, 0.2, 1)")
                    t(TransitionProperty.OPACITY, duration = ms(180), timing = TimingFunction.EaseOut)
                }
            }
        }

        // 2) Non-WebKit browsers (Chrome / Firefox) - use pre-blurred bgLayer + tint overlay to emulate the glass effect
        "@supports not (-webkit-backdrop-filter: blur(1px))" style {
            // Ensure header creates isolated compositing context
            ".site-header" style {
                position(Position.Relative)
                isolation(Isolation.Isolate)
                contain(Contain.Paint)
                // remove any reliance on backdrop-filter in non-webkit browsers
                background("transparent")
                // Forcefully disable backdrop-filter if any inline style remains
                property(
                    "backdrop-filter",
                    "none !important"
                )
                property(
                    "-webkit-backdrop-filter",
                    "none !important"
                )
            }

            // NOTE: Try to apply the injected SVG filter to the bg-layer. This uses filter:url(#header-glass-filter).
            // Some browsers require the URL to be absolute or the SVG to be in the document; Header.kt injects the SVG defs into the DOM.
            // If filter:url doesn't apply in some environments, fallback to a light CSS filter on the canvas (blur done in Canvas drawing path already).
            ".header-bg-layer" style {
                position(Position.Absolute)
                inset(0.px)
                zIndex(0)
                pointerEvents(PointerEvents.None)
                property(
                    "transform",
                    "translateZ(0)"
                )
                willChange(WillChange.Transform, WillChange.Opacity, WillChange.Filter)
                property(
                    "backface-visibility",
                    "hidden"
                )
                contain(Contain.Paint)
                transformOrigin("center")
                // IMPORTANT: disable SVG/CSS filters in non-WebKit to avoid visual artifacts
                property(
                    "filter",
                    "none !important"
                )
                // Previously attempted: property("filter", "url(#header-glass-filter)") -- removed due to artifacts
            }

            // Apply a subtle contrast/saturation tweak to the canvas content only (no blur)
            ".header-bg-layer canvas" style {
                // gentle enhancement to better match Safari's backdrop-filter saturation/contrast
                property(
                    "filter",
                    "contrast(1.04) saturate(1.06)"
                )
                // IMPORTANT: Do not add blur() here
            }

            // Tint overlay sits above the blurred bg-layer to simulate translucent glass
            ".header-blur-overlay" style {
                position(Position.Absolute)
                inset(0.px)
                zIndex(1)
                pointerEvents(PointerEvents.None)
                // tint color mirrors the original rgba used in Header.kt (theme-aware overrides exist inline)
                background("rgba(255,255,255,0.18)")
                property(
                    "mix-blend-mode",
                    "normal"
                )
                willChange(WillChange.Transform, WillChange.Opacity)
                property(
                    "transform",
                    "translateZ(0)"
                )
                property(
                    "backface-visibility",
                    "hidden"
                )
                contain(Contain.Paint)
                isolation(Isolation.Isolate)
                property(
                    "outline",
                    "1px solid rgba(255,0,0,0.25)"
                )
            }

            // Foreground content layer
            ".header-content-layer" style {
                position(Position.Relative)
                zIndex(2)
                willChange(WillChange.Transform, WillChange.Opacity)
            }

            // Transition classes (same transform+opacity rules)
            ".site-header.site-header--init" style {
                transparent()
                property(
                    "transform",
                    "translateY(6px)"
                )
            }
            ".site-header.site-header--ready" style {
                opaque()
                property(
                    "transform",
                    "translateY(0)"
                )
                transition {
                    t(TransitionProperty.TRANSFORM, duration = ms(220), customTiming = "cubic-bezier(0.2, 0.0, 0.2, 1)")
                    t(TransitionProperty.OPACITY, duration = ms(180), timing = TimingFunction.EaseOut)
                }
            }
        }

        // Global reduced-motion handling: disable transitions where applicable
        "@media (prefers-reduced-motion: reduce)" style {
            ".site-header.site-header--ready" style {
                property(
                    "transition",
                    "none"
                )
            }
            ".header-blur-overlay" style {
                property(
                    "transition",
                    "none"
                )
            }
            ".header-bg-layer" style {
                property(
                    "transition",
                    "none"
                )
                property(
                    "filter",
                    "none"
                )
            }
        }
    }
}
